/**
 * Share-link hand-off (backend#985). The public shared-assignment page on the
 * BondIT website links to `/consultant-signup?share=<token>` ("Apply") and to
 * `/login?share=<token>` ("Log in").
 *
 * The token is captured when the app boots (`main.tsx` imports this module
 * early), kept in `sessionStorage` so it survives reloads and every step of
 * the signup wizard, and removed from the address bar so analytics never
 * record it. It is sent as `share` with the signup start request and with
 * assignment applications from this tab. The backend only uses it for
 * attribution: an unknown or expired token is ignored, never rejected.
 *
 * Arriving through `/login` additionally leaves a one-shot redirect: after
 * signing in, the login page resolves the token and opens the assignment
 * (#1072), then forgets the redirect. The attribution copy stays, so an
 * application sent afterwards is still credited to the link.
 */
export const SHARE_PARAM = "share";
export const SHARE_STORAGE_KEY = "bondit.shareToken";
export const SHARE_REDIRECT_STORAGE_KEY = "bondit.shareRedirect";
const SIGNUP_PATH = "/consultant-signup";
const LOGIN_PATH = "/login";

// The backend accepts at most 64 characters; tokens are lower-case letters and
// digits in dash-separated groups. Anything else is dropped rather than sent,
// so a mangled link can never make the signup request fail validation.
const TOKEN_PATTERN = /^[A-Za-z0-9-]{1,64}$/;

/** The token in a canonical-ish form, or null when it can't be a share token. */
export const normalizeShareToken = (value: string | null | undefined): string | null => {
  const token = value?.trim();
  return token && TOKEN_PATTERN.test(token) ? token : null;
};

/**
 * Move a `?share=` token from the signup or login URL into session storage.
 * Idempotent: without the parameter it changes nothing.
 */
export const captureShareToken = (): void => {
  try {
    const path = window.location.pathname.replace(/\/+$/, "");
    if (path !== SIGNUP_PATH && path !== LOGIN_PATH) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (!params.has(SHARE_PARAM)) {
      return;
    }
    const token = normalizeShareToken(params.get(SHARE_PARAM));
    if (token) {
      window.sessionStorage.setItem(SHARE_STORAGE_KEY, token);
      if (path === LOGIN_PATH) {
        window.sessionStorage.setItem(SHARE_REDIRECT_STORAGE_KEY, token);
      }
    }
    params.delete(SHARE_PARAM);
    const rest = params.toString();
    const { pathname, hash } = window.location;
    window.history.replaceState(
      window.history.state,
      "",
      pathname + (rest ? `?${rest}` : "") + hash
    );
  } catch (error) {
    // Storage can be blocked; signup and login still work, only unattributed.
    console.warn("Could not read the share link:", error);
  }
};

const readToken = (key: string): string | null => {
  try {
    return normalizeShareToken(window.sessionStorage.getItem(key));
  } catch {
    return null;
  }
};

/** The share token this tab arrived with, if any. */
export const getShareToken = (): string | null => readToken(SHARE_STORAGE_KEY);

/** The shared assignment to open once the visitor has signed in, if any. */
export const getShareRedirectToken = (): string | null => readToken(SHARE_REDIRECT_STORAGE_KEY);

/** Forget the post-login redirect; it is used at most once. */
export const clearShareRedirectToken = (): void => {
  try {
    window.sessionStorage.removeItem(SHARE_REDIRECT_STORAGE_KEY);
  } catch {
    // Blocked storage never held a redirect in the first place.
  }
};

captureShareToken();
